import {
    columnOrderingFeature,
    columnPinningFeature,
    columnResizingFeature,
    columnSizingFeature,
    columnVisibilityFeature,
    createExpandedRowModel,
    rowExpandingFeature,
    rowSelectionFeature,
    tableFeatures,
} from "@tanstack/react-table";
import type {
    Cell,
    CellContext,
    Column,
    ColumnDef,
    Header,
    HeaderContext,
    ReactTable,
    Row,
    RowData,
    Table,
} from "@tanstack/react-table";

export const viewTableFeatures = tableFeatures({
    columnOrderingFeature,
    columnPinningFeature,
    columnSizingFeature,
    columnResizingFeature,
    columnVisibilityFeature,
    rowSelectionFeature,
    rowExpandingFeature,
    expandedRowModel: createExpandedRowModel(),
});

export type ViewTableFeatures = typeof viewTableFeatures;

export type ViewTableInstance<TData extends RowData> = Table<
    ViewTableFeatures,
    TData
>;
export type ViewTableReactInstance<TData extends RowData> = ReactTable<
    ViewTableFeatures,
    TData
>;
export type ViewTableRow<TData extends RowData> = Row<ViewTableFeatures, TData>;
export type ViewTableColumn<TData extends RowData, TValue = unknown> = Column<
    ViewTableFeatures,
    TData,
    TValue
>;
export type ViewTableColumnDef<
    TData extends RowData,
    TValue = unknown,
> = ColumnDef<ViewTableFeatures, TData, TValue>;
export type ViewTableHeader<TData extends RowData, TValue = unknown> = Header<
    ViewTableFeatures,
    TData,
    TValue
>;
export type ViewTableHeaderContext<
    TData extends RowData,
    TValue = unknown,
> = HeaderContext<ViewTableFeatures, TData, TValue>;
/**
 * The context leaf header cells render with. `ViewTable` reads page-row
 * selection fresh each render: the core table stays the same object when
 * rows load or selection changes, so a compiled header can't rely on its
 * getters. TanStack's header template takes the plain context, so a template
 * narrows with `isLeafHeaderContext` and renders nothing without the flags.
 */
export interface ViewTableLeafHeaderContext<
    TData extends RowData,
    TValue = unknown,
> extends ViewTableHeaderContext<TData, TValue> {
    isAllPageRowsSelected: boolean;
    isSomePageRowsSelected: boolean;
}

export const isLeafHeaderContext = <TData extends RowData, TValue>(
    context: ViewTableHeaderContext<TData, TValue>,
): context is ViewTableLeafHeaderContext<TData, TValue> =>
    "isAllPageRowsSelected" in context && "isSomePageRowsSelected" in context;
export type ViewTableCell<TData extends RowData, TValue = unknown> = Cell<
    ViewTableFeatures,
    TData,
    TValue
>;
export type ViewTableCellContext<
    TData extends RowData,
    TValue = unknown,
> = CellContext<ViewTableFeatures, TData, TValue>;
